import React, { useEffect, useState } from "react";
import CommonFooter from "../../components/footer/CommonFooter";
import CheckoutProduct from "./component/checkoutProduct/index.js";
import PaymentModal from "./component/paymentModal/index.js";
import OffersModal from "./component/offersModal/index.js";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import FullScreenLoader from "../../components/loader/fullScreenLoader/FullscreenLoader";
import CommonButton from "../../components/button/CommonButton";
import {
  initializeRazorpayCheckout,
  getRazorpayConfig,
} from "../../utils/razorpay";
import "./Checkout.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { appApiHandler } from "../../api/appApiHandler";
import { getPrice } from "../../constants/globalFunction.js";
import { showErrorToast, showSuccessToast } from "../../utils/toast.js";
import { string } from "../../constants/string.js";
import HomeHeader from "../../components/header/HomeHeader";
import { RUPEE } from "../../constants/globalConstants";
import { setCartCount } from "../../store/slices/countState.js";
import Breadcrumb from "../../components/breadcrumb";
import EmptyState from "../../components/EmptyState/";
import { FaUser, FaShoppingCart, FaTag, FaAngleRight } from "react-icons/fa";
import { FaCircleCheck } from "react-icons/fa6";
import moment from "moment";

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { address } = location.state;
  const { userData } = useSelector((state) => state.userState);
  const { isLoggedIn } = useSelector((state) => state.authState);

  const [cartItems, setCartItems] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [subTotal, setSubTotal] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [cod, setCod] = useState(true);
  const [online, setOnline] = useState(true);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [showConfirmation, setShowOrderConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [offerModalVisible, setOfferModalVisible] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const getCartProducts = async () => {
    try {
      const reqData = {
        user_id: userData?._id,
      };
      const response = await appApiHandler.getCart(reqData);
      if (response?.status === 200 && response?.data) {
        setCartItems(response?.data?.products);
        dispatch(setCartCount(response?.data?.products?.length));
        if (response?.data?.tax_rates && address?.country_id) {
          const countryTaxRate = response?.data?.tax_rates?.find(
            (taxRate) => taxRate?.country_id === address?.country_id
          );
          if (countryTaxRate) {
            if (userData?.user_type === "wholeseller") {
              setTaxRate(countryTaxRate?.wholesale_tax_rate || 0);
            } else {
              setTaxRate(countryTaxRate?.retail_tax_rate || 0);
            }
          } else {
            setTaxRate(0);
          }
        }
        setCod(response?.data?.cod);
        setOnline(response?.data?.online);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const isCouponValidToday = (coupon) => {
    const expiryDate = coupon?.expiry_date;
    const today = moment().startOf("day");

    if (!expiryDate) {
      return true;
    }

    const expiry = moment(expiryDate, "DD-MM-YYYY", true);
    if (!expiry.isValid()) return false;

    return expiry.isSameOrAfter(today, "day");
  };

  const getCouponDiscount = (coupon, amount) => {
    if (!coupon || !amount) return 0;

    if (coupon?.discount_type === "flat") {
      const flat = parseFloat(coupon?.discount_amount) || 0;
      return Math.min(flat, amount);
    }

    const percentage = parseFloat(coupon?.discount_percentage) || 0;
    const discount = (amount * percentage) / 100;
    return Math.min(discount, amount);
  };

  const getAllCoupons = async () => {
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getAllCoupons(reqData);
    if (response?.status === 200 && response?.data) {
      const couponsData = Array.isArray(response?.data) ? response.data : [];
      setCoupons(couponsData.filter(isCouponValidToday));
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      setProductLoading(true);
      const fetchAllData = async () => {
        try {
          await Promise.all([
            getCartProducts(),
            getAllCoupons()
          ]);
        } catch (error) {
          console.log(error);
        } finally {
          setProductLoading(false);
        }
      };
      fetchAllData();
    }
  }, [isLoggedIn]);

  const getProductPrice = async (size) => {
    const price = await getPrice(size);
    return price;
  };

  useEffect(() => {
    const calculateTotalAmount = async () => {
      const itemTotals = await Promise.all(
        cartItems?.map(async (item) => {
          const price = await getProductPrice(item?.size);
          return price * item?.qty;
        })
      );
      const total = itemTotals.reduce((acc, curr) => acc + curr, 0);
      setSubTotal(total);
    };
    calculateTotalAmount();
  }, [cartItems]);

  useEffect(() => {
    const taxableAmount = (subTotal * taxRate) / 100;
    setTaxAmount(taxableAmount);
  }, [taxRate, subTotal]);

  useEffect(() => {
    if (
      selectedCoupon &&
      !coupons?.some((coupon) => coupon?._id === selectedCoupon?._id)
    ) {
      setSelectedCoupon(null);
    }
  }, [coupons, selectedCoupon]);

  const couponDiscount = getCouponDiscount(selectedCoupon, subTotal);
  const remainingAmount = Math.max(0, subTotal - couponDiscount) + taxAmount;

  const handlePaymentModal = () => {
    if (remainingAmount === 0) {
      const reqData = {
        user_id: userData?._id,
        address_id: address?._id,
        paid: 1,
        total: remainingAmount?.toFixed(2),
        tax_rate: taxRate,
        coupon_id: selectedCoupon?._id,
      };
      setLoading(true);
      placeOrder(reqData);
      return;
    }

    if (userData?.user_type === "wholeseller") {
      setShowOrderConfirmation(true);
    } else {
      setPaymentModalVisible(true);
    }
  };

  const handlePayment = async (method) => {
    setPaymentModalVisible(false);
    if (method === 0) {
      const reqData = {
        user_id: userData?._id,
        address_id: address?._id,
        payment_method: "COD",
        paid: 0,
        total: remainingAmount?.toFixed(2),
        tax_rate: taxRate,
        coupon_id: selectedCoupon?._id,
      };
      setLoading(true);
      placeOrder(reqData);
    } else {
      setLoading(true);
      const orderData = {
        amount: remainingAmount * 100,
        currency: "INR",
        user_id: userData?._id,
      };
      const response = await appApiHandler.createOrder(orderData);
      if (response?.status === 200 && response?.order_id) {
        proceedToCheckout(response?.order_id);
      } else if (response?.message) {
        setLoading(false);
        showErrorToast(response?.message);
      } else {
        setLoading(false);
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    }
  };

  const placeOrder = async (reqData) => {
    const response = await appApiHandler.placeOrder(reqData);
    setLoading(false);
    if (response?.status === 200) {
      showSuccessToast(response?.message);
      navigate("/");
    } else if (response?.message) {
      showErrorToast(response?.message);
    } else {
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const proceedToCheckout = async (orderId) => {
    setLoading(true);

    try {
      const config = getRazorpayConfig();

      const response = await initializeRazorpayCheckout({
        ...config,
        amount: remainingAmount * 100,
        order_id: orderId,
        prefill: {
          contact: address?.contact,
          email: userData?.email,
          name: userData?.name,
        },
      })
        .then((data) => {
          if (data?.razorpay_payment_id) {
            const reqData = {
              user_id: userData?._id,
              address_id: address?._id,
              payment_method: "Online",
              paid: 1,
              total: remainingAmount?.toFixed(2),
              tax_rate: taxRate,
              payment_id: data?.razorpay_payment_id,
              coupon_id: selectedCoupon?._id,
            };
            setLoading(true);
            placeOrder(reqData);
          } else {
            setLoading(false);
            showErrorToast("Payment failed. Please try again.");
          }
        })
        .catch((error) => {
          console.error("Razorpay payment gateway error:", error);
          setLoading(false);

          if (
            error?.message?.includes("cancelled") ||
            error?.message?.includes("canceled")
          ) {
            showErrorToast("Payment was cancelled");
          } else if (error?.message?.includes("failed")) {
            showErrorToast("Payment failed. Please try again.");
          } else if (
            error?.message?.includes("network") ||
            error?.message?.includes("connection")
          ) {
            showErrorToast(
              "Network error. Please check your connection and try again."
            );
          } else {
            showErrorToast("Payment failed. Please try again.");
          }
        });
    } catch (error) {
      console.error("Razorpay payment error:", error);
      setLoading(false);
      if (error?.code === 0) {
        showErrorToast("Payment cancelled");
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    }
  };

  const handleOrderConfirmation = () => {
    setShowOrderConfirmation(false);
    const reqData = {
      user_id: userData?._id,
      address_id: address?._id,
      total: remainingAmount?.toFixed(2),
      tax_rate: taxRate,
      coupon_id: selectedCoupon?._id,
    };
    setLoading(true);
    placeOrder(reqData);
  };

  const onConfirmationCancel = () => {
    setShowOrderConfirmation(false);
  };

  if (productLoading) {
    return (
      <div className="checkout-container">
        <HomeHeader />
        <Breadcrumb
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: "Checkout",
              isActive: true,
            },
          ]}
        />
        <div className="checkout-loading">
          <div className="loading-spinner"></div>
          <p>Loading cart items...</p>
        </div>
        <CommonFooter />
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <HomeHeader />
      <div className="page-content">
        <Breadcrumb
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: "Checkout",
              isActive: true,
            },
          ]}
        />
        {!isLoggedIn ? (
          <EmptyState
            icon={FaUser}
            title="Sign In to Continue"
            description="please login with your account. Enjoy a seamless shopping experience with personalized recommendations and more!"
            buttonText="Login"
            onButtonClick={() => navigate("/login")}
            iconSize={48}
          />
        ) : (
          <div className="checkout-content">
            <div className="checkout-scroll">
              {cartItems.map((item, index) => (
                <CheckoutProduct key={index} item={item} />
              ))}

              {coupons.length > 0 && (
                <div
                  className="address-container offers-container"
                  onClick={() => setOfferModalVisible(true)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={`offers-content${selectedCoupon ? " offers-content-applied" : ""}`}>
                    {selectedCoupon ? (
                      <>
                        <div className="offers-row offers-available-row">
                          <div className="offers-left">
                            <FaTag className="offers-icon" />
                            <span className="offers-text">
                              Available offers ({coupons.length})
                            </span>
                          </div>
                          <FaAngleRight className="offers-arrow" />
                        </div>
                        <div className="offers-row offers-applied-row">
                          <div className="offers-left offers-applied-left">
                            <span className="offers-code">{selectedCoupon?.code}</span>
                            <div className="offers-applied-status">
                              <FaCircleCheck className="offers-applied-icon" size={14} />
                              <span className="offers-text offers-applied-label">Applied</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="offers-remove-button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedCoupon(null);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="offers-left">
                          <FaTag className="offers-icon" />
                          <span className="offers-text">Available offers ({coupons.length})</span>
                        </div>
                        <FaAngleRight className="offers-arrow" />
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="address-container">
                <h3 className="address-title">Shipping Address</h3>
                <div className="address-card">
                  <p className="name-text">{address?.name}</p>
                  <p className="address-text">{address?.address}</p>
                  <p className="address-text">{address?.city}</p>
                  <p className="address-text">
                    {address?.state}, {address?.country?.name} -{" "}
                    {address?.pincode}
                  </p>
                  <p className="address-text">
                    Contact No: {address?.country_code}
                    {address?.contact}
                  </p>
                </div>
              </div>

              {(taxAmount > 0 || selectedCoupon) && (
                <div className="address-container">
                  <h3 className="address-title">Payment Summary</h3>
                  <div className="address-card">
                    <div className="summary-container">
                      <span className="title-text">Sub Total</span>
                      <span className="value-text">
                        {RUPEE}
                        {subTotal?.toFixed(2)}
                      </span>
                    </div>
                    {taxAmount > 0 && (
                      <div className="summary-container">
                        <span className="title-text">Tax Rate ({taxRate}%)</span>
                        <span className="value-text">
                          {RUPEE}
                          {taxAmount?.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {selectedCoupon && (
                      <div className="summary-container">
                        <span className="title-text">Discount</span>
                        <span className="value-text">
                          -{RUPEE}
                          {couponDiscount?.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {selectedCoupon && (
                      <div className="summary-container">
                        <span className="title-text">Total</span>
                        <span className="value-text">
                          {RUPEE}
                          {remainingAmount?.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="total-amount-container">
                <span className="total-amount">Payable Amount</span>
                <span className="total-amount-text">
                  {RUPEE}
                  {remainingAmount?.toFixed(2)}
                </span>
              </div>

              <CommonButton
                title="Proceed to Checkout"
                onPress={handlePaymentModal}
                customClassName="checkout-button"
                icon={FaShoppingCart}
              />
            </div>
          </div>
        )}

        <PaymentModal
          visible={paymentModalVisible}
          onClose={() => setPaymentModalVisible(false)}
          onHandleSelect={handlePayment}
          cod={cod}
          online={online}
        />

        <ConfirmationModal
          visible={showConfirmation}
          title="Confirmation"
          description="Are you sure you want to place this order?"
          rightButtonText="Yes"
          leftButtonText="No"
          onRightButtonPress={handleOrderConfirmation}
          onLeftButtonPress={onConfirmationCancel}
        />

        <OffersModal
          visible={offerModalVisible}
          onClose={() => setOfferModalVisible(false)}
          coupons={coupons}
          subTotal={subTotal}
          selectedCoupon={selectedCoupon}
          onApplyCoupon={(coupon) => setSelectedCoupon(coupon)}
          onRemoveCoupon={() => setSelectedCoupon(null)}
        />

        {loading && <FullScreenLoader text="Placing order..." />}
      </div>
      <CommonFooter />
    </div>
  );
};

export default Checkout;
